import { UNIQUES, awakenedFx, type SkillId, type UniqueId } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { DIRS, hexToWorld, worldToHex } from '../hex.ts';
import { dist2, idiv, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { skillLevelBp } from './create.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, isFlying, targetable } from './combat.ts';
import { stunFor } from './towers.ts';
import { featBoar, featLamp, featMantle, featPierce, featUse } from './feats.ts';
import { axeEye, axePullEye, axeRadius, boarWoke, formFinal, bowLance, bowNumbers, followed, formB, formBits, formsHeroTick, keyBeam, keyHoldFast, lampForms, mantleFreeze, mantleRadius, mantleRose, mantleWakes } from './forms.ts';
import { UNIQUE_FORMS } from '@somnhold/content';

/**
 * The saga's unique pieces in a fight (docs/QUESTS.md): each one changes how a skill of its class
 * works. Here the ones that run on their own every tick; the rest hook into their skill.
 */

export const hasUq = (h: Hero, id: UniqueId): boolean => !!h.uq?.includes(id);

/** A unique's numbers, grown with its awakening stage on this hero (AWAKEN.powerStepBp). */
export const fx = (id: UniqueId, h?: Hero): Record<string, number> => {
  const i = h?.uq?.indexOf(id) ?? -1;
  const lv = i >= 0 ? (h?.uqLv?.[i] ?? 0) : 0;
  return lv > 0 ? awakenedFx(UNIQUES[id].fx, lv, UNIQUES[id]) : UNIQUES[id].fx;
};

/** How much of its own recharge or length (`key`, bp) a skill gets from the uniques worn that change it. */
export const uqBp = (h: Hero, skill: SkillId, key: 'cooldownBp' | 'durationBp'): number => {
  let bp = 10000;
  for (const id of h.uq ?? []) {
    if (UNIQUES[id].skill !== skill) continue;
    if (UNIQUES[id].fx[key] !== undefined) bp = mulBp(bp, fx(id, h)[key] as number);
    // A form of the piece that changes it too (the early dawn's recharge).
    const fb = UNIQUE_FORMS[id].b?.fx[key];
    if (fb !== undefined && formB(h, id)) bp = mulBp(bp, fb);
    // A final form that changes it (the mender's thimble: the staff comes back, docs/PANOPLIA.md §10).
    const ff = UNIQUE_FORMS[id].final.fx[key];
    if (ff !== undefined && formFinal(h, id)) bp = mulBp(bp, ff);
  }
  return bp;
};

/** A heavy blow's stun (the gate blade, Ser Gaspar's hammer): whole on the big ones, a third on elites, never on what nothing stuns. */
export const heavyStun = (e: Enemy, stun: number): number => (e.kind === 'dragon' ? stunFor(e, stun) : e.elite ? (stunFor({ ...e, elite: false }, 3) > 0 ? idiv(stun, 3) : 0) : stunFor(e, 3) > 0 ? stun : 0);

/** Whether a point lies within `w` of the segment a–b (fixed units). */
export const nearSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number, w: number): boolean => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  if (t < 0) t = 0;
  if (t > 1000) t = 1000;
  const cx = ax + idiv(dx * t, 1000);
  const cy = ay + idiv(dy * t, 1000);
  return dist2(px, py, cx, cy) <= w * w;
};

/** The hexes of a ring of ice around a hex: the hex and its neighbours, and the next ring out for a wider one. */
const RING2 = [
  { q: 2, r: 0 }, { q: 2, r: -1 }, { q: 2, r: -2 }, { q: 1, r: -2 }, { q: 0, r: -2 }, { q: -1, r: -1 },
  { q: -2, r: 0 }, { q: -2, r: 1 }, { q: -2, r: 2 }, { q: -1, r: 2 }, { q: 0, r: 2 }, { q: 1, r: 1 },
];

/** Every tick, for a standing hero: the frost mantle and the boar hide wake on low life. */
export const uniqueHeroTick = (s: BattleState, h: Hero): void => {
  if (!h.uq?.length) return;
  if (h.mantleCd && h.mantleCd > 0) h.mantleCd--;
  if (h.boarCd && h.boarCd > 0) h.boarCd--;
  const low = (bp: number): boolean => h.hp * 10000 < h.stats.maxHp * bp;
  // Its forms: the winter's crowd wakes it too, the glacier is wider and freezes elites, the counterfrost chills who strikes.
  if (hasUq(h, 'frostMantle') && !h.mantleCd && (h.uqForm ? mantleWakes(s, h, low(fx('frostMantle', h).belowBp as number)) : low(fx('frostMantle', h).belowBp as number))) {
    // A ring of ice on the cells around the mage: whoever is in it freezes, and the mage can't be hurt a moment.
    const f = fx('frostMantle', h);
    const radius = mantleRadius(h, f.radius as number);
    const c = worldToHex(h.x, h.y);
    const cells: number[] = [];
    for (const d of radius > (f.radius as number) ? [{ q: 0, r: 0 }, ...DIRS, ...RING2] : [{ q: 0, r: 0 }, ...DIRS]) {
      const p = hexToWorld(c.q + d.q, c.r + d.r);
      const id = cellAtWorld(s, p.x, p.y);
      if (id >= 0) cells.push(id);
    }
    s.zones.push({ id: s.nextId++, kind: 'frost', x: h.x, y: h.y, radius: 0, cells, ticks: f.ticks as number, every: 1, damage: 0, slowBp: f.slowBp as number, pid: h.pid });
    const frozen: Enemy[] = [];
    for (const e of enemiesNear(s, h.x, h.y, radius, (o) => targetable(s, o) && o.kind !== 'boneWall')) {
      const st = h.uqForm ? mantleFreeze(s, h, e, f.stun as number) : stunFor(e, f.stun as number);
      if (e.stun < st) e.stun = st;
      if (st > 0) frozen.push(e);
    }
    featMantle(s, h, frozen);
    if (h.invuln < (f.invuln as number)) h.invuln = f.invuln as number;
    ev(s, { t: 'boom', x: h.x, y: h.y, r: radius, kind: 'frostMantle' });
    h.mantleCd = f.every as number;
    if (h.uqForm) mantleRose(s, h, f.ticks as number);
  }
  if (hasUq(h, 'boarHide') && !h.boarCd && low(fx('boarHide', h).belowBp as number)) {
    h.boarCd = fx('boarHide', h).every as number;
    h.frenzy = 8 * 12;
    h.stun = 0;
    featBoar(s, h);
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 900, kind: 'frenzy' });
    if (h.uqForm) boarWoke(s, h);
  }
  if (h.uqForm) formsHeroTick(s, h, (slot) => castSlot(s, h, slot));
};

/** Casts a button's skill as if its player pressed it (the medal's oath that goes off by itself). */
let castSlot: (s: BattleState, h: Hero, slot: 0 | 1 | 2) => boolean = () => false;
export const setCastSlot = (f: typeof castSlot): void => {
  castSlot = f;
};

/** The garrison key: an artificer's turrets close to each other are joined by a burning beam. */
export const turretLinks = (s: BattleState): void => {
  for (const h of s.heroes) {
    if (!hasUq(h, 'garrisonKey')) continue;
    const f = fx('garrisonKey', h);
    if (s.tick % (f.every as number) !== 0) continue;
    const mine = s.turrets.filter((t) => t.owner === h.id);
    for (let i = 0; i < mine.length; i++)
      for (let j = i + 1; j < mine.length; j++) {
        const a = mine[i]!;
        const b = mine[j]!;
        if (!withinRange(a.x, a.y, b.x, b.y, f.link as number)) continue;
        const mx = idiv(a.x + b.x, 2);
        const my = idiv(a.y + b.y, 2);
        const half = idiv(isqrt(dist2(a.x, a.y, b.x, b.y)), 2) + (f.width as number);
        const crossed: Enemy[] = [];
        for (const e of enemiesNear(s, mx, my, half, (o) => targetable(s, o))) {
          if (!nearSegment(e.x, e.y, a.x, a.y, b.x, b.y, f.width as number)) continue;
          featUse(s, h, 'garrisonKey', damageEnemy(s, e, mulBp(f.damage as number, skillLevelBp(h.level)), { pid: h.pid, ignoresArmor: true }));
          crossed.push(e);
        }
        ev(s, { t: 'shot', from: a.id, to: b.id, kind: 'link', x: a.x, y: a.y, tx: b.x, ty: b.y });
        // Its forms: the slowing beam, the discharge.
        if (h.uqForm) keyBeam(s, h, a, b, crossed, (v) => mulBp(v, skillLevelBp(h.level)), f.every as number);
      }
    // The hold fast: three joined wear out slower.
    if (h.uqForm && formBits(h, 'garrisonKey') & 4) keyHoldFast(s, h, f.link as number, f.every as number);
  }
};

/** The chapel lamp: a cleric's blow heals the most hurt ally nearby by a share of it. */
export const lampHeal = (s: BattleState, h: Hero, dealt: number): void => {
  if (dealt <= 0 || !hasUq(h, 'chapelLamp')) return;
  const f = fx('chapelLamp', h);
  let best: Hero | null = null;
  let bh = 0;
  for (const o of s.heroes) {
    if (o.downTicks > 0 || o.stats.maxHp <= 0 || !withinRange(h.x, h.y, o.x, o.y, f.range as number)) continue;
    const miss = idiv((o.stats.maxHp - o.hp) * 10000, o.stats.maxHp);
    if (miss > bh) {
      bh = miss;
      best = o;
    }
  }
  if (best) {
    const was = best.hp;
    const heal = mulBp(dealt, f.healBp as number);
    const got = healHero(s, best, heal, h.pid);
    featLamp(s, h, best, got, was);
    // Its forms: the high lamp heals a second, the ward of light keeps what was left over.
    if (h.uqForm) lampForms(s, h, best, heal, got, f.range as number);
  }
};

/** The watchtower bow: every few arrows one goes through the whole line, harder, and marks whoever it crosses. */
export const watchShot = (s: BattleState, h: Hero, e: Enemy, dmg: number): boolean => {
  if (!hasUq(h, 'watchBow')) return false;
  const f = fx('watchBow', h);
  // Its forms: the flurry goes through more often and softer, the deep mark marks harder and longer.
  const [every, damageBp, markBp, markTicks] = h.uqForm ? bowNumbers(h, f.every as number, f.damageBp as number, f.markBp as number, f.markTicks as number) : [f.every as number, f.damageBp as number, f.markBp as number, f.markTicks as number];
  h.shots = (h.shots ?? 0) + 1;
  if (h.shots % every !== 0) return false;
  const d = isqrt(dist2(h.x, h.y, e.x, e.y)) || 1;
  const L = f.length as number;
  const tx = h.x + idiv((e.x - h.x) * L, d);
  const ty = h.y + idiv((e.y - h.y) * L, d);
  let through = 0;
  const pierced: Enemy[] = [];
  for (const o of enemiesNear(s, idiv(h.x + tx, 2), idiv(h.y + ty, 2), idiv(L, 2) + (f.width as number), (x) => targetable(s, x))) {
    if (!nearSegment(o.x, o.y, h.x, h.y, tx, ty, f.width as number)) continue;
    through++;
    damageEnemy(s, o, mulBp(dmg, damageBp), { pid: h.pid, pierce: true });
    o.markTicks = markTicks;
    o.markBp = markBp;
    pierced.push(o);
  }
  ev(s, { t: 'shot', from: h.id, to: e.id, kind: 'pierceShot', x: h.x, y: h.y, tx, ty });
  // The watchtower's lance pins the last it reached.
  if (h.uqForm) bowLance(s, h, pierced);
  featPierce(s, h, through);
  return true;
};

/** The wanderer's cloak: after the net, a leap back away from the netted. */
export const leapBack = (s: BattleState, h: Hero, fromX: number, fromY: number): void => {
  const d = isqrt(dist2(h.x, h.y, fromX, fromY)) || 1;
  const L = fx('wandererCloak', h).leap as number;
  for (const k of [10000, 6000, 3000]) {
    const x = h.x + idiv(mulBp(h.x - fromX, k) * L, d);
    const y = h.y + idiv(mulBp(h.y - fromY, k) * L, d);
    if (!heroWalkable(s, cellAtWorld(s, x, y))) continue;
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 400, kind: 'leapFrom' });
    h.x = h.destX = h.ax = x;
    h.y = h.destY = h.ay = y;
    h.path = [];
    h.moving = false;
    return;
  }
};

/** The wall axe: the whirl drags the nightmares around in toward the barbarian (elites too, never the ones nothing stuns). Returns how many it pulled. */
export const whirlPull = (s: BattleState, h: Hero): number => {
  let pulled = 0;
  const f = fx('wallAxe', h);
  // Its forms: the anchor pulls from farther, the eye of the hurricane pulls bosses and flyers too.
  const radius = h.uqForm ? axeRadius(h, f.radius as number) : (f.radius as number);
  // The eye of the hurricane never moves the dragon of the Burn: its pull on it is a dry blow on the glass (§4.3).
  if (h.uqForm && formFinal(h, 'wallAxe')) for (const e of enemiesNear(s, h.x, h.y, radius, (o) => o.kind === 'dragon' && targetable(s, o))) e.dry = true;
  for (const e of enemiesNear(s, h.x, h.y, radius, (o) => targetable(s, o) && (o.elite || stunFor(o, 3) > 0 || (!!h.uqForm && axeEye(h, o))) && o.kind !== 'boneWall' && o.kind !== 'dragon')) {
    const d = isqrt(dist2(e.x, e.y, h.x, h.y));
    const to = f.to as number;
    if (d <= to) continue;
    if (h.uqForm && axeEye(h, e) && (isFlying(e) || !(e.elite || stunFor(e, 3) > 0))) {
      if (axePullEye(s, h, e, to)) pulled++;
      continue;
    }
    const x = h.x + idiv((e.x - h.x) * to, d);
    const y = h.y + idiv((e.y - h.y) * to, d);
    if (!heroWalkable(s, cellAtWorld(s, x, y))) continue;
    e.x = x;
    e.y = y;
    e.next = -1;
    pulled++;
  }
  return pulled;
};

/** Zones that follow their caster (storm, sanctuary, hallowed ground) keep to it. */
export const followZones = (s: BattleState): void => {
  for (const z of s.zones) {
    if (!z.follow) continue;
    const h = s.heroes.find((o) => o.id === z.follow);
    if (!h || h.downTicks > 0) continue;
    // The still eye: how long the mage stood.
    if (z.fm) followed(z, h);
    z.x = h.x;
    z.y = h.y;
  }
};

/** The roots of the old staff spread from these to their neighbours (once), holding as long as the first. Returns how many more it held. */
export const rootSpread = (s: BattleState, held: Enemy[], hold: number, h?: Hero, out?: Enemy[]): number => {
  let spread = 0;
  const r = fx('oldRootStaff', h).radius as number;
  const seen = new Set(held.map((e) => e.id));
  for (const e of held) {
    for (const o of enemiesNear(s, e.x, e.y, r, (x) => targetable(s, x) && x.kind !== 'boneWall' && !seen.has(x.id))) {
      seen.add(o.id);
      if (o.slowBp < 10000) o.slowBp = o.elite ? 5000 : 10000;
      if (o.slowTicks < hold) o.slowTicks = hold;
      ev(s, { t: 'boom', x: o.x, y: o.y, r: 400, kind: 'entangle' });
      out?.push(o);
      spread++;
    }
  }
  return spread;
};
